/**
 * TikTok OAuth 2.0 Authentication with PKCE
 *
 * ⚠️ CRITICAL PRE-VERIFICATION RESTRICTION:
 *
 * **SANDBOX MODE (Current State):**
 * - Only users added as "Test Users" in TikTok Developer Portal can connect
 * - Regular users will get `access_denied` or `unauthorized_client` error
 * - No warning message - just complete authorization failure
 *
 * **How to Add Test Users:**
 * 1. Go to TikTok Developer Portal: https://developers.tiktok.com/
 * 2. Select your app → Settings → Test Users
 * 3. Add users by TikTok username or email
 * 4. Send invitation (users must accept)
 * 5. Max ~20 test users per app
 *
 * **After Production Approval:**
 * - Submit app for production review in TikTok Developer Portal (7-14 days)
 * - Once approved, ALL users can connect (no test user restriction)
 * - Posts can be PUBLIC (must update privacyLevel in postContent.ts)
 *
 * **PKCE (Proof Key for Code Exchange):**
 * - Required by TikTok for security
 * - code_verifier: Random 43-128 character string (generated here)
 * - code_challenge: Base64-URL-encoded SHA256 hash of verifier
 * - Verifier stored in localStorage, sent in token exchange
 *
 * See /backend/OAUTH_SCOPES.md for full documentation
 */
import env from '@/config/env'
import { trackPlatformConnectStarted } from '@/lib/analytics/connectionTracking'

/**
 * TikTok OAuth Scopes
 * - user.info.basic: Basic user info (name, avatar)
 * - user.info.profile: Extended profile data
 * - video.upload: Upload video content
 * - video.publish: Create and publish videos
 */
const SCOPE = [
  'user.info.basic',
  'user.info.profile',
  'user.info.stats',
  'video.upload',
  'video.publish',
  'video.list',
] as const

export const TIKTOK_AUTH_STATE_STORAGE_KEY = 'tiktok_auth_state' as const
export const TIKTOK_CODE_VERIFIER_STORAGE_KEY = 'tiktok_code_verifier' as const
export type AuthState = { rnd: string; referrerPath: string }

/**
 * Generate a random code verifier for PKCE (43-128 characters)
 */
function generateCodeVerifier(): string {
  const array = new Uint8Array(32)
  crypto.getRandomValues(array)
  return base64UrlEncode(array)
}

/**
 * Generate code challenge from verifier using SHA-256
 */
async function generateCodeChallenge(verifier: string): Promise<string> {
  const encoder = new TextEncoder()
  const data = encoder.encode(verifier)
  const hash = await crypto.subtle.digest('SHA-256', data)
  return base64UrlEncode(new Uint8Array(hash))
}

/**
 * Base64 URL encode without padding
 */
function base64UrlEncode(array: Uint8Array): string {
  const base64 = btoa(String.fromCharCode(...array))
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '')
}

export const startAuthProcess = async (opts: Omit<AuthState, 'rnd'>) => {
  const redirectPath = '/auth/tiktok'

  const state: AuthState = {
    ...opts,
    rnd: Math.random().toString().slice(3, 12),
  }

  // Generate PKCE parameters
  const codeVerifier = generateCodeVerifier()
  const codeChallenge = await generateCodeChallenge(codeVerifier)

  const url = new URL('https://www.tiktok.com/v2/auth/authorize')
  const params = new URLSearchParams({
    client_key: env.VITE_TIKTOK_CLIENT_ID,
    scope: SCOPE.join(','),
    response_type: 'code',
    redirect_uri: `${window.location.origin}${redirectPath}`,
    state: JSON.stringify(state),
    code_challenge: codeChallenge,
    code_challenge_method: 'S256',
  })

  const fullUrl = `${url}?${params}`

  // Store state and code verifier for callback
  localStorage.setItem(TIKTOK_AUTH_STATE_STORAGE_KEY, JSON.stringify(state))
  localStorage.setItem(TIKTOK_CODE_VERIFIER_STORAGE_KEY, codeVerifier)

  const storedState = localStorage.getItem(TIKTOK_AUTH_STATE_STORAGE_KEY)
  if (!storedState) {
    console.error('Failed to store TikTok auth state')
    return
  }

  trackPlatformConnectStarted('tiktok')
  window.location.href = fullUrl
}
